// Plan v7 / Day 24 F6 — useLocale hook.
//
// Thin wrapper around MTDB's `useSelectedLocale()` (bootstrap-data driven)
// that exposes the current AppLocale, the list of locales the SITE has
// activated server-side, plus a `switchTo()` helper that translates the
// current URL via localizeUrl() and triggers a full page navigation.
//
// We use full page navigation (not SPA reload) because the locale's
// translation lines are server-rendered into bootstrap-data; the new
// locale needs a fresh HTML payload.

import {useCallback} from 'react';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useBootstrapDataStore} from '@ui/bootstrap-data/bootstrap-data-store';
import {
  APP_LOCALES,
  type AppLocale,
  DEFAULT_LOCALE,
  isAppLocale,
} from './locale-config';
import {localizeUrl} from './localize-url';

export interface UseLocaleReturn {
  /** Current active locale (lowercased ISO 639-1; e.g. 'tr', 'en', 'ar', 'es', 'ru'). */
  current: AppLocale;
  /** All locales the application supports (TR + 4). */
  available: readonly AppLocale[];
  /** Locales currently activated on the server (per-locale active flag, F3 middleware). */
  active: readonly AppLocale[];
  /** Switch to a target locale, translating the current URL via localizeUrl(). */
  switchTo: (target: AppLocale) => void;
}

/**
 * Returns active locales injected from the server bootstrap data
 * (AppBootstrapData::init() — `i18n_active` key).
 * Falls back to ['tr'] if the field is missing (defensive).
 */
function useActiveLocales(): readonly AppLocale[] {
  const fromBoot = useBootstrapDataStore(
    s => (s.data as unknown as {i18n_active?: unknown})?.i18n_active,
  );
  if (Array.isArray(fromBoot)) {
    const list = fromBoot.filter(isAppLocale);
    if (list.length > 0) return list;
  }
  return ['tr'];
}

export function useLocale(): UseLocaleReturn {
  const {localeCode} = useSelectedLocale();
  const current: AppLocale = isAppLocale(localeCode) ? localeCode : DEFAULT_LOCALE;
  const active = useActiveLocales();

  const switchTo = useCallback(
    (target: AppLocale) => {
      if (target === current) return;
      if (typeof window === 'undefined') return;

      // Detay sayfalarında (kişi/başlık/haber) her dilin KENDİ per-locale slug'ı
      // hreflang alternate link'lerinde hazır (sunucu üretir + client meta günceller).
      // Önce onu kullan → hedef dilin DOĞRU slug'ına DOĞRUDAN git. Aksi halde
      // localizeUrl yalnız segment'i çevirip KAYNAK slug'ı koruyordu
      // (/en/person/url-slug-en → /ar/shakhs/url-slug-en) → sunucuda pahalı
      // fallback çözümü, tek 301 hop ve (deploy öncesi) 404 riski. hreflang ile
      // doğrudan /ar/shakhs/url-slug-ar'a gidilir (hızlı, kayıpsız, döngüsüz).
      const alt = document.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${target}"]`,
      );
      if (alt) {
        const href = alt.getAttribute('href') || '';
        try {
          const u = new URL(href, window.location.origin);
          if (u.origin === window.location.origin && u.pathname.length > 1) {
            window.location.assign(u.pathname + u.search + u.hash);
            return;
          }
        } catch {
          // geçersiz href → localizeUrl fallback
        }
      }

      const here = window.location.pathname + window.location.search + window.location.hash;
      const next = localizeUrl(here, target, {currentLocale: current});
      window.location.assign(next);
    },
    [current],
  );

  return {
    current,
    available: APP_LOCALES,
    active,
    switchTo,
  };
}
